/**
 * DeleteMemberModal - Modal de confirmação para exclusão de membro
 * Responsabilidade: Confirmar exclusão permanente e avisar sobre desatribuição de dados
 * 
 * Princípios: Componentização, Single Responsibility
 * Similar ao DeleteConnectionModal para consistência visual
 */
import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { AppIcon } from '../../../components/icons/AppIcon';

interface DeleteMemberModalProps {
    isOpen: boolean;
    memberName: string;
    memberEmail: string;
    onConfirm: () => Promise<void>;
    onCancel: () => void;
}

const DeleteMemberModal: React.FC<DeleteMemberModalProps> = ({
    isOpen,
    memberName,
    memberEmail,
    onConfirm,
    onCancel
}) => {
    const [isDeleting, setIsDeleting] = useState(false);

    const handleConfirm = async () => {
        setIsDeleting(true);
        try {
            await onConfirm();
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onCancel}
            size="md"
            preventBackdropClose={isDeleting}
        >
            {/* Header customizado vermelho */}
            <div className="bg-red-600 px-5 py-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <AppIcon name="alert-triangle" className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white">
                    ATENÇÃO - Exclusão permanente!
                </h3>
            </div>

            <Modal.Body>
                <p className="text-default">
                    Você está prestes a excluir o membro:
                </p>

                <div className="mt-3 p-3 bg-surface-subtle rounded-lg border border-subtle">
                    <p className="font-semibold text-default">{memberName || 'Sem nome'}</p>
                    <p className="text-sm text-muted">{memberEmail}</p>
                </div>

                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                        <AppIcon name="info" className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div className="text-sm text-amber-800">
                            <p className="font-medium">O que acontece ao excluir:</p>
                            <ul className="mt-1 list-disc list-inside space-y-1 text-amber-700">
                                <li>Conversas atribuídas ficarão sem atribuição</li>
                                <li>Tarefas atribuídas ficarão sem atribuição</li>
                                <li>Negociações atribuídas ficarão sem atribuição</li>
                                <li><strong>Nenhum dado será apagado</strong>, apenas desatribuído</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800 text-sm">
                        Esta ação é <strong>permanente e irreversível</strong>. O usuário perderá
                        acesso à organização imediatamente.
                    </p>
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button
                    variant="ghost"
                    onClick={onCancel}
                    disabled={isDeleting}
                >
                    Cancelar
                </Button>
                <Button
                    variant="danger"
                    onClick={handleConfirm}
                    disabled={isDeleting}
                >
                    {isDeleting ? (
                        <span className="flex items-center gap-2">
                            <AppIcon name="loader" className="w-4 h-4 animate-spin" />
                            Excluindo...
                        </span>
                    ) : (
                        'Excluir permanentemente'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default DeleteMemberModal;
